import { Input, VStack } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { useState } from "react"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "@hydrologiq/shared/utils/toast"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import {
  CreateSystemDocument,
  type CreateSystemMutation,
  type CreateSystemMutationVariables,
} from "@hydrologiq/gql-experience/graphql"
import {
  CustomerSiteSelectorProvider,
  useCustomerSiteSelectorState,
  useSystemName,
} from "@hydrologiq/shared/components/selection/CustomerSiteSelectorProvider"
import CustomerSiteSelector from "@hydrologiq/shared/components/selection/CustomerSiteSelector"

export interface SystemCreationFormProps {
  onSuccess: (system: { id: string; name: string }) => void
  onCancel?: () => void
  onBack?: () => void
  /** Pre-set customer ID (hides customer selection) */
  customerId?: string
  /** Pre-set site ID (hides site selection) */
  siteId?: string
  /** Whether to show customer and site selection fields */
  showCustomerSiteSelection?: boolean
  /** Callback to navigate to customer creation screen */
  onCreateCustomer?: () => void
  /** Callback to navigate to site creation screen */
  onCreateSite?: () => void
  /**
   * When true, does NOT create its own CustomerSiteSelectorProvider.
   * Use this when the form is already wrapped in an external provider
   * (e.g. inside a MultiScreenDialog that manages customer/site state).
   */
  externalProvider?: boolean
}

/**
 * Shared system creation form component.
 * When showCustomerSiteSelection is true, the user selects a customer and site
 * via the generic CustomerSiteSelector.
 * When customerId and siteId are pre-set, only the system name is required.
 */
const SystemCreationForm = (props: SystemCreationFormProps) => {
  const {
    customerId: presetCustomerId,
    siteId: presetSiteId,
    externalProvider = false,
  } = props

  if (externalProvider) {
    return <SystemCreationFormInner {...props} />
  }

  return (
    <CustomerSiteSelectorProvider
      initialCustomerId={presetCustomerId || null}
      initialSiteId={presetSiteId || null}
    >
      <SystemCreationFormInner {...props} />
    </CustomerSiteSelectorProvider>
  )
}

const SystemCreationFormInner = ({
  onSuccess,
  onCancel,
  onBack,
  customerId: presetCustomerId,
  siteId: presetSiteId,
  showCustomerSiteSelection = false,
  onCreateCustomer,
  onCreateSite,
}: SystemCreationFormProps) => {
  const [error, setError] = useState<string | null>(null)

  // System name state lives in the provider — persists across screen transitions
  const { systemName, setSystemName } = useSystemName()

  const { customerId: selectedCustomerId, siteId: selectedSiteId } = useCustomerSiteSelectorState()

  const [createSystem, { loading }] = useMutationWithEndpoint<
    CreateSystemMutation,
    CreateSystemMutationVariables
  >(
    CreateSystemDocument,
    {
      update(cache, { data }) {
        if (!data?.createSystem) return
        // Evict systems list queries so they refetch on next mount
        cache.evict({ fieldName: "getSystems" })
        cache.evict({ fieldName: "getSystemsList" })
        cache.gc()
      },
    },
    "primary",
  )

  const handleSubmit = async () => {
    let loadingToastId: string | undefined
    try {
      setError(null)

      loadingToastId = showLoadingToast("Creating system", "Adding system...")

      const name = systemName.trim()
      if (!name) {
        const errorMessage = "Please provide a system name"
        setError(errorMessage)
        showErrorToast("Validation error", errorMessage)
        return false
      }

      // Determine customer/site to pass
      const customerId = presetCustomerId || selectedCustomerId || undefined
      const siteId = presetSiteId || selectedSiteId || undefined

      // If customer/site selection is required, validate
      if (showCustomerSiteSelection && !presetCustomerId) {
        if (!customerId) {
          const errorMessage = "Please select a customer"
          setError(errorMessage)
          showErrorToast("Validation error", errorMessage)
          return false
        }
        if (!siteId) {
          const errorMessage = "Please select a site"
          setError(errorMessage)
          showErrorToast("Validation error", errorMessage)
          return false
        }
      }

      const result = await createSystem({
        variables: {
          input: {
            name,
            customerId: customerId || null,
            siteId: siteId || null,
          },
        },
      })

      if (result.errors && result.errors.length > 0) {
        let errorMessage = result.errors[0].message || "Failed to create system. Please try again."

        if (errorMessage.includes("duplicate key") || errorMessage.includes("already exists")) {
          errorMessage = "A system with this name already exists. Please choose a different name."
        }

        setError(errorMessage)
        showErrorToast("Creation failed", errorMessage)
        return false
      }

      if (result.data?.createSystem) {
        const createdSystem = result.data.createSystem
        showSuccessToast("System created", `System "${name}" has been created successfully.`)
        setError(null)
        setSystemName("")
        onSuccess({ id: createdSystem.id, name: createdSystem.name })
        return true
      } else {
        const errorMessage = "Failed to create system. The server returned an unexpected response."
        setError(errorMessage)
        showErrorToast("Creation failed", errorMessage)
        return false
      }
    } catch (error) {
      let errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred while creating the system"

      if (errorMessage.includes("duplicate key") || errorMessage.includes("already exists")) {
        errorMessage = "A system with this name already exists. Please choose a different name."
      }

      setError(errorMessage)
      showErrorToast("Creation failed", errorMessage)
      return false
    } finally {
      if (loadingToastId) {
        dismissToast(loadingToastId)
      }
    }
  }

  const handleCancel = () => {
    setError(null)
    setSystemName("")
    if (onCancel) onCancel()
  }

  const handleBack = () => {
    setError(null)
    setSystemName("")
    if (onBack) onBack()
  }

  return (
    <Form
      heading="Create a system"
      helperText="Please provide system information below."
      actionType="create"
      actionContent={{ label: "Create", description: "Create the system" }}
      loading={loading}
      onSubmit={handleSubmit}
      onCancel={onCancel ? handleCancel : undefined}
      onBack={onBack ? handleBack : undefined}
    >
      <VStack gap={5}>
        {error && (
          <ErrorAlert
            title="System Creation Error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        <Field label="System name" required>
          <Input
            name="name"
            value={systemName}
            onChange={(e) => setSystemName(e.target.value)}
            placeholder="Enter system name"
          />
        </Field>

        {showCustomerSiteSelection && (
          <CustomerSiteSelector
            hideCustomer={!!presetCustomerId}
            hideSite={!!presetSiteId}
            onCreateCustomer={onCreateCustomer}
            onCreateSite={onCreateSite}
          />
        )}
      </VStack>
    </Form>
  )
}

export default SystemCreationForm
